import { useState } from "react";
import { Stack, Select, MenuItem, InputLabel, FormControl, OutlinedInput } from "@material-ui/core";
import { preventBubbling } from "utils/listings/helper";
import Label from "components/Label";
import { CheckedMenuItemLabel } from "./CheckedMenuItemLabel";
import type { SelectChangeEvent } from "@material-ui/core";
import type { LabelColor } from "components/Label";

type SimpleMultiSelectProps = {
  label?: string;
  value: string[];
  small?: boolean;
  options: string[];
  loading?: boolean;
  emptyLabel?: string;
  // Close the dropdown after each pick — for multi-selects where most users
  // pick a single value (e.g. customer type). Default keeps the menu open.
  closeOnSelect?: boolean;
  onChange: (value: string[]) => void;
  getOptionLabel?: (option: string) => string;
  // Per-option chip color for the selected values (e.g. customer-type colors).
  getOptionColor?: (option: string) => LabelColor;
};

export function SimpleMultiSelect({
  label,
  value,
  small,
  options,
  loading,
  emptyLabel,
  closeOnSelect,
  onChange,
  getOptionLabel,
  getOptionColor
}: SimpleMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const handleChange = ({ target: { value } }: SelectChangeEvent<string[]>): void => {
    if (typeof value === "string") return;

    onChange(value);

    if (closeOnSelect) setOpen(false);
  };

  const handleDelete = (menuValue: string) => (): void => {
    const filteredValue = value.filter((value) => value !== menuValue);

    onChange(filteredValue);
  };

  const useEmptyLabel = value.length === 0 && emptyLabel;

  if (useEmptyLabel) {
    label = emptyLabel;
  }

  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        size={small ? "small" : undefined}
        value={value}
        input={<OutlinedInput label={label} />}
        disabled={loading}
        open={closeOnSelect ? open : undefined}
        onOpen={closeOnSelect ? () => setOpen(true) : undefined}
        onClose={closeOnSelect ? () => setOpen(false) : undefined}
        renderValue={(selected) => (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {selected.map((value) => (
              <Label
                color={getOptionColor ? getOptionColor(value) : "primary"}
                variant="ghost"
                onDelete={handleDelete(value)}
                onMouseDown={preventBubbling()}
              >
                {getOptionLabel ? getOptionLabel(value) : value}
              </Label>
            ))}
          </Stack>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 410
            }
          }
        }}
        onChange={handleChange}
      >
        {options?.map((name) => (
          <MenuItem value={name} key={name}>
            <CheckedMenuItemLabel selected={value.includes(name)}>
              {getOptionLabel ? getOptionLabel(name) : name}
            </CheckedMenuItemLabel>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
